
import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    computed,
    effect,
    forwardRef,
    inject,
    signal,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormBuilder,
    FormGroup,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import {
    MatFormFieldAppearance,
    MatFormFieldModule,
} from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import {
    Item,
    SelectFieldComponent,
} from '../select-field/select-field.component';
import { ListaRuoliStore } from './ruoli-selector.store';
import { ListaStruttureStore } from './strutture-selector.store';

/**
 * The value shape of the struttura/ruolo selector's reactive form group.
 */
export interface StrutturaRuoloValue {
  struttura: Item | null;
  ruolo: Item | null;
}

@UntilDestroy()
@Component({
   
  selector: 'siat-struttura-ruoli-selector',
  templateUrl: './struttura-ruoli-selector.component.html',
  styleUrls: ['./struttura-ruoli-selector.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    SelectFieldComponent,
    TranslatePipe
],
  providers: [
    ListaRuoliStore,
    ListaStruttureStore,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StrutturaRuoliSelectorComponent),
      multi: true,
    },
  ],
})
export class StrutturaRuoliSelectorComponent
  implements OnInit, ControlValueAccessor
{
  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() id?: string;
  @Input() name?: string;
  @Input() label: string;
  @Input() placeholder?: string;
  @Output() optionReady = new EventEmitter<boolean>();

  protected storeRuoli = inject(ListaRuoliStore);
  protected storeStrutture = inject(ListaStruttureStore);
  private fb = inject(FormBuilder);

  formGroup: FormGroup = this.fb.group({
    struttura: [null],
    ruolo: [null],
  });

  // Usare signals per rendere reattivo il binding
  private strutture = signal<Item[]>([]);
  private ruoli = signal<Item[]>([]);
  private isInitialValueSet = false;

  // Computed: direttamente leggibili dal template
  public itemsStrutture = computed(() => this.strutture());
  public itemsRuoli = computed(() => this.ruoli());

  // ---- CVA ----
  private onChange: (value: StrutturaRuoloValue) => void = () => {};
  private onTouched: () => void = () => {};

  registerOnChange(fn: (value: StrutturaRuoloValue) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.formGroup.disable();
    } else {
      this.formGroup.enable();
    }
  }

  writeValue(value: StrutturaRuoloValue | null): void {
    if (value) {
      this.isInitialValueSet = true;
      this.formGroup.patchValue(value, { emitEvent: false });
    } else {
      this.isInitialValueSet = false;
      this.formGroup.reset();
    }
  }

  constructor() {
    effect(() => {
      const ruoli = this.storeRuoli.listaRuoliItemResponse();
      if (ruoli) this.ruoli.set(ruoli);
    });

    effect(() => {
      const strutture = this.storeStrutture.listaStruttureItemResponse();
      if (strutture) this.strutture.set(strutture);
    });
  }

  ngOnInit(): void {
    // Carica le opzioni
    this.storeRuoli.fetchGetAllRoles('ruoli');
    this.storeStrutture.fetchGetAllStrutture('strutture');

    // Clear ruolo when struttura changes, but not during initial value setting
    this.formGroup.get('struttura')?.valueChanges.subscribe(() => {
      if (!this.isInitialValueSet) {
        const ruoloControl = this.formGroup.get('ruolo');
        if (ruoloControl) {
          ruoloControl.setValue(null);
          ruoloControl.markAsPristine();
          ruoloControl.markAsUntouched();
        }
      }
      // Reset the flag after initial setup
      this.isInitialValueSet = false;
    });

    // Propaga cambiamenti al padre
    this.formGroup.valueChanges.subscribe(val => {
      this.onChange(val);
      this.onTouched();
    });
  }
}
